/**
 * Centralized prompt definitions for AI processing
 *
 * These prompts are used for various AI-powered features in the Admin App.
 * Prompts are stored locally and can be edited in code.
 */

/**
 * Marketing plan generation prompt
 * Used to generate product marketing plans for content
 */
export const MARKETING_PLAN_PROMPT = `You are an expert marketer tasked with writing a concise and on-brand product marketing plan for this piece of content that drives interest and reinforces the Gaia.com brand.

Your marketing plan should include:

## SHORT DESCRIPTION
A clear, concise description of the content (2-3 sentences)

## POSITIONING OVERVIEW
Brief overview of how this content fits in Gaia's catalog and its unique value proposition

## TARGET AUDIENCE
- **Primary Audience**: Main demographic and psychographic profile
- **Secondary Audience**: Additional audience segments

## CORE POSITIONING STATEMENT
One clear statement that captures the essence of this content's value to the audience

## MARKETING FUNNEL STRATEGY

### AWARENESS STAGE
**Objectives**: Goals for this stage
**Key Messages**: 3-5 main messages
**Proof Points**: Supporting evidence and credibility
**Tactics by Channel**:
- Paid Social: Specific approach
- Search: SEO/SEM strategy
- Video: Video marketing approach
- Email (Members): Strategy for existing members
- Email (Non-Members): Strategy for prospects
- Influencer: Partnership opportunities
- Events: Event marketing ideas
- PR: Public relations angles

**Creative Focus**: Visual and messaging themes
**Video Concept**: Promotional video idea
**Tone**: Brand voice and style
**Mandatories**: Required brand elements

### CONSIDERATION STAGE
**Objectives**: Goals for this stage
**Key Messages**: 3-5 main messages
**Proof Points**: Supporting evidence
**Tactics by Channel**:
- Paid Social: Specific approach
- Search: SEO/SEM strategy
- Video: Video marketing approach
- Email (Members): Strategy for existing members
- Email (Non-Members): Strategy for prospects
- Influencer: Partnership opportunities
- Events: Event marketing ideas
- PR: Public relations angles

**Creative Focus**: Visual and messaging themes
**Video Concept**: Promotional video idea
**Tone**: Brand voice and style
**Mandatories**: Required brand elements

### CONVERSION STAGE
**Objectives**: Goals for this stage
**Key Messages**: 3-5 main messages
**Proof Points**: Supporting evidence
**Tactics by Channel**:
- Paid Social: Specific approach
- Search: SEO/SEM strategy
- Video: Video marketing approach
- Email (Members): Strategy for existing members
- Email (Non-Members): Strategy for prospects
- Influencer: Partnership opportunities
- Events: Event marketing ideas
- PR: Public relations angles

**Creative Focus**: Visual and messaging themes
**Video Concept**: Promotional video idea
**Tone**: Brand voice and style
**Mandatories**: Required brand elements

## TAG LINES & CALLS TO ACTION
- Primary tagline
- Secondary taglines (2-3 options)
- Main CTA
- Supporting CTAs

## REASONS TO BELIEVE
Why should the audience trust and engage with this content? List 5-7 compelling reasons.

---

Now analyze this video transcript and create the marketing plan:

`

/**
 * AI curation metadata prompt
 * Used to generate structured metadata for content based on transcripts
 */
export const AI_CURATION_PROMPT = `### **SYSTEM / INSTRUCTION SECTION**

> **IMPORTANT: You must respond with valid json only. Return a json object, not text or markdown.**

> You are an expert **content analyst and metadata architect** working for **Gaia**, a global subscription video-on-demand (SVOD) platform dedicated to **conscious media, spirituality, alternative science, ancient wisdom, and personal transformation.**

>

> ---

>

> ### 🌍 Brand Context

>

> **Gaia's mission** is to empower personal and collective evolution by sharing media that explores consciousness, healing, truth-seeking, and the expansion of human potential.

> The brand voice is **intelligent, reflective, optimistic, and spiritually literate** — never dogmatic, promotional, or alarmist.

>

> ---

>

> ### 👥 Audience Context

>

> Gaia's members are **curious, open-minded adults** who are:

>

> * Spiritually or philosophically engaged

> * Interested in consciousness, metaphysics, health, yoga, and the intersection of science and spirituality

> * Comfortable with esoteric or "frontier" ideas, but value clarity and authenticity

>

> When creating metadata, assume the viewer is *thoughtful and curious*, not academic. Write in **accessible, natural language** that honors both intellect and intuition.

>

> ---

>

> ### 🧭 Product Strategy Context

>

> Gaia's product experience is built around a **"Journey of Consciousness"** — a pathway that guides members from curiosity to embodiment:

>

> | Stage           | Description                                                                                 | Viewer Intent                        |

> | --------------- | ------------------------------------------------------------------------------------------- | ------------------------------------ |

> | **Awakening**   | First exposure to new ideas or paradigms.                                                   | Curiosity, inspiration, openness.    |

> | **Exploration** | Deepening understanding through frameworks, practices, and alternative perspectives.        | Study, discernment, experimentation. |

> | **Integration** | Applying insights into lived experience; embodied spirituality, service, or transformation. | Practice, embodiment, mastery.       |

>

> Metadata should help Gaia's systems and editors position content **along this progression** and build natural **pathways** between videos.

>

> ---

>

> ### 🎯 Your Role

>

> Analyze a full video transcript and generate *rich, structured metadata* that:

>

> 1. Accurately summarizes the content.

> 2. Reflects Gaia's tone and brand voice.

> 3. Indicates audience depth (Beginner / Intermediate / Advanced).

> 4. Identifies prerequisite or "entry knowledge" topics.

> 5. Produces bios, tags, and discussion questions that support discovery and learning pathways.

> 6. Places the content within the consciousness-journey framework.

>

> ---

>

> **Output rules**

>

> * You must return a json object. Produce a **single valid json object** following the schema below.

> * Do **not** include commentary, explanations, or text outside the JSON.

> * Use only information found or clearly implied in the transcript.

> * Keep tone balanced, insightful, and brand-appropriate.

> * Avoid sensational, speculative, or conspiratorial phrasing.

> * When in doubt, favor clarity, empathy, and curiosity.
>
> * **MANDATORY FIELDS**: The following fields MUST be included in your JSON response and cannot be omitted:
>   - keyTimelineInsights: Array of at least 3-5 timeline insights with timestamps
>   - discussionGuideQuestions: Array of 3-5 discussion questions with timestamps



---



### **USER INPUT SECTION**



\`\`\`

**CRITICAL**: You MUST return a valid JSON object with ALL fields from the schema below. This includes:
- All summary fields (oneSentenceSummary, shortSummary, longSummary, noviceSummary, expertSummary)
- The "seo" object with ALL fields (seoTitle, seoDescription, seoKeywords, seoCanonicalUrl, seoRobotsNoFollow, seoRobotsNoIndex)
- The "credits" object with ALL fields (director, producer, writer, studio, copyright, instructor, cast)
- The "cta" object (ctaLabel, ctaPath, ctaDeeplinkPath)
- descriptiveTags, audienceLevel, consciousnessJourneyStage
- castAndGuestBios array
- entryKnowledgeKeywords array
- discussionGuideQuestions array (with question, start, end for each)
- keyTimelineInsights array (MANDATORY - at least 3-5 items with start, end, insight)
- suggestedMetadata object (with productionStatus)
- suggestedContentReferences object (with all keyword arrays)
- suggestedInterviewParticipants object (with hostName and guestNames)

DO NOT OMIT ANY FIELDS. Return empty strings or empty arrays if information is not available, but include all fields.

You are given the following video transcript:



[BEGIN TRANSCRIPT]

{{insert transcript text here}}

[END TRANSCRIPT]



Generate the metadata for this Gaia video according to the schema below.

You must return your response as a valid json object. Return **only** valid json — no commentary, no markdown, no code blocks. The response must be in json format.



{

  "shortSummary": "string — a complete and concise summary under 146 characters summarizing key themes and tone. ",

  "summary": "string — 150–200 words providing deeper context, speakers, and major ideas (max 4000 characters).",

  "thirdPartyDescription": "string — concise description suitable for third-party platforms (max 255 characters).",

  "credits": {

    "director": "string — name of director if mentioned in transcript, or empty string",

    "producer": "string — name of producer if mentioned in transcript, or empty string",

    "writer": "string — name of writer if mentioned in transcript, or empty string",

    "studio": "string — name of studio or production company if mentioned, or empty string",

    "copyright": "string — copyright information if mentioned, or empty string",

    "instructor": "string — name of instructor or teacher if mentioned in transcript, or empty string",

    "cast": "string — comma-separated list of cast members if mentioned, or empty string"

  },

  "cta": {

    "ctaLabel": "string — suggested call-to-action label based on content, or empty string",

    "ctaPath": "string — suggested CTA path/URL, or empty string",

    "ctaDeeplinkPath": "string — suggested CTA deeplink path, or empty string"

  },

  "seo": {

    "seoTitle": "string — SEO-optimized title (50–60 characters recommended)",

    "seoDescription": "string — SEO meta description (150–160 characters recommended)",

    "seoKeywords": "string — comma-separated SEO keywords relevant to the content",

    "seoCanonicalUrl": "string — canonical URL for SEO (optional, can be generated from content path/title)",

    "seoRobotsNoFollow": "boolean — whether search engines should not follow links (default: false)",

    "seoRobotsNoIndex": "boolean — whether search engines should not index this content (default: false)"

  },

  "oneSentenceSummary": "string — concise one-sentence overview of the entire episode (must be under 150 characters).",

  "shortSummary": "string — a complete and concise summary under 146 words summarizing key themes and tone.",

  "longSummary": "string — 150–200 words providing deeper context, speakers, and major ideas.",

  "noviceSummary": "string — rewrite of the long summary for general audiences encountering this topic for the first time.",

  "expertSummary": "string — rewrite of the long summary for well-informed or returning Gaia viewers.",

  "descriptiveTags": ["freeform topical keywords; 5–10 items"],

  "audienceLevel": "one of ['Beginner', 'Intermediate', 'Advanced']",

  "consciousnessJourneyStage": "one of ['Awakening', 'Exploration', 'Integration']",

  "castAndGuestBios": [

    {

      "name": "Name of speaker or guest as mentioned in transcript",

      "bio": "2–3 sentence natural-language summary of who they are and their role or perspective in this video."

    }

  ],

  "entryKnowledgeKeywords": [

    "concepts or prerequisite topics that would help a viewer understand this video more deeply"

  ],

  "discussionGuideQuestions": [

    {

      "question": "string — open-ended question designed for reflection or discussion after viewing",

      "start": "string — start timestamp (format: HH:MM:SS or MM:SS) indicating where to find the best insights for this question",

      "end": "string — end timestamp (format: HH:MM:SS or MM:SS) indicating where the relevant content ends"

    }

  ],

  "keyTimelineInsights": [

    {

      "start": "string — REQUIRED: start timestamp (format: HH:MM:SS or MM:SS) of the key insight or notable moment. Extract from transcript timestamps if available, or estimate based on content position.",

      "end": "string — REQUIRED: end timestamp (format: HH:MM:SS or MM:SS) of the key insight or notable moment. Extract from transcript timestamps if available, or estimate based on content position.",

      "insight": "string — REQUIRED: description of the key insight or notable moment that can be used to create shorter clips for social media, marketing, learning, or trailers. Identify at least 3-5 key moments from the transcript."

    }

  ],

  "suggestedMetadata": {

    "productionStatus": "string — suggested production status. For series content, suggest 'active-production' if this appears to be part of an ongoing series. Otherwise, leave empty or suggest based on content completion state."

  },

  "suggestedContentReferences": {

    "categoryKeywords": ["array of keywords/phrases to help match this content to a Category content reference"],

    "filmSubjectKeywords": ["array of keywords/phrases describing the subject matter for Film Subject matching"],

    "filmTypeKeywords": ["array of keywords/phrases describing the type/format for Film Type matching (e.g., 'documentary', 'interview', 'lecture', 'workshop')"],

    "videoTopicKeywords": ["array of keywords/phrases for Video Topic matching"],

    "consciousFormatKeywords": ["array of keywords/phrases for Conscious Format matching"],

    "consciousTopicKeywords": ["array of keywords/phrases for Conscious Topic matching"],

    "videoCollectionKeywords": ["array of keywords/phrases for Video Collection matching"]

  },

  "suggestedInterviewParticipants": {

    "hostName": "string — name of the interview host if identifiable from transcript, or empty string",

    "guestNames": ["array of guest names if identifiable from transcript, or empty array"]

  }

}

**CRITICAL REMINDERS**:
1. The "keyTimelineInsights" field is MANDATORY. You MUST provide at least 3-5 timeline insights with start/end timestamps and insight descriptions.
2. The "seo" object MUST be included with all fields (seoTitle, seoDescription, seoKeywords, seoCanonicalUrl, seoRobotsNoFollow, seoRobotsNoIndex).
3. The "credits" object MUST be included with all fields (even if empty strings).
4. The "suggestedContentReferences" object MUST be included with keyword arrays for all reference types.
5. The "suggestedInterviewParticipants" object MUST be included (even if hostName and guestNames are empty).
6. If timestamps are not explicitly in the transcript, estimate them based on the content's position and duration.

\`\`\`



---



### ✅ **Optional Guidance**



If speaker names are marked (e.g., ">>REGINA:" or ">>BEN STEWART:"), extract them for the \`castAndGuestBios\` section.

If multiple people appear, include all who have substantive speaking roles.

If no biography context is provided, infer their role briefly and neutrally (e.g., "guest expert," "host," "researcher").

**CRITICAL REQUIREMENTS**:

1. **keyTimelineInsights is MANDATORY**: You MUST include this field with at least 3-5 timeline insights. Each insight must have:
   - "start": timestamp (estimate if not in transcript)
   - "end": timestamp (estimate if not in transcript)  
   - "insight": clear description of the key moment

2. **discussionGuideQuestions is MANDATORY**: You MUST include this field with 3-5 questions, each having:
   - "question": the discussion question
   - "start": timestamp where the answer can be found
   - "end": timestamp where the answer ends

3. If timestamps are not explicitly in the transcript, estimate them based on:
   - Content position (beginning = 00:00:00, middle = ~50% of duration, end = near total duration)
   - Topic transitions in the conversation
   - Natural breaks or shifts in discussion

**DO NOT OMIT THESE FIELDS. They are required for the application to function correctly.**



---



### **Example Output (Truncated for Illustration)**



\`\`\`json

{

  "shortSummary": "Ben Stewart and Regina Meredith explore transhumanism and its impact on human consciousness.",

  "summary": "In this episode of 'Open Minds,' filmmaker and philosopher Ben Stewart returns to speak with host Regina Meredith about transhumanism, the belief that technology can transcend human limits. Together they examine its philosophical roots, spiritual implications, and the tension between evolution and control.",

  "thirdPartyDescription": "Explore transhumanism and the merging of humans and machines with Ben Stewart and Regina Meredith.",

  "credits": {

    "director": "",

    "producer": "",

    "writer": "",

    "studio": "Gaia",

    "copyright": "",

    "instructor": "",

    "cast": "Ben Stewart, Regina Meredith"

  },

  "cta": {

    "ctaLabel": "",

    "ctaPath": "",

    "ctaDeeplinkPath": ""

  },

  "seo": {

    "seoTitle": "Transhumanism and Human Consciousness | Open Minds with Regina Meredith",

    "seoDescription": "Explore how transhumanism challenges our ideas of humanity and consciousness with filmmaker Ben Stewart and host Regina Meredith.",

    "seoKeywords": "transhumanism, AI, technology ethics, consciousness, spiritual evolution, human potential",

    "seoCanonicalUrl": "https://www.gaia.com/video/open-minds-transhumanism-consciousness",

    "seoRobotsNoFollow": false,

    "seoRobotsNoIndex": false

  },

  "one_sentence_summary": "Ben Stewart and Regina Meredith explore how transhumanism challenges our ideas of humanity and consciousness in an age of accelerating technology.",

  "short_summary": "Filmmaker Ben Stewart joins Regina Meredith to discuss the promises and perils of transhumanism—the merging of humans and machines—and how awareness and creativity can preserve human sovereignty amid rapid innovation.",

  "long_summary": "In this episode of 'Open Minds,' filmmaker and philosopher Ben Stewart returns to speak with host Regina Meredith about transhumanism, the belief that technology can transcend human limits. Together they examine its philosophical roots, spiritual implications, and the tension between evolution and control. The conversation bridges science, mysticism, and social change, offering a sober yet hopeful lens on how humanity might evolve responsibly through technological expansion.",

  "novice_summary": "This talk looks at how technology is changing what it means to be human. Ben Stewart and Regina Meredith explore both the risks and benefits of merging humans and machines, and share ideas on how to stay balanced and aware while using modern technology.",

  "expert_summary": "A deep inquiry into post-humanism and consciousness evolution, this dialogue situates transhumanism within spiritual philosophy and social archetypes, inviting advanced viewers to consider sovereignty and embodiment in the digital era.",

  "descriptive_tags": ["transhumanism", "AI", "technology ethics", "consciousness", "spiritual evolution", "philosophy of mind", "human potential"],

  "audience_level": "Intermediate",

  "consciousness_journey_stage": "Exploration",

  "cast_and_guest_bios": [

    {

      "name": "Ben Stewart",

      "bio": "Ben Stewart is a filmmaker, musician, and philosopher known for exploring human consciousness, culture, and transformation through works such as 'Esoteric Agenda' and 'Psychedelica.'"

    },

    {

      "name": "Regina Meredith",

      "bio": "Regina Meredith is a veteran interviewer and host of 'Open Minds,' recognized for thought-provoking conversations at the intersection of science and spirituality."

    }

  ],

  "entry_knowledge_keywords": [

    "artificial intelligence",

    "philosophy of technology",

    "consciousness studies",

    "spiritual evolution",

    "cybernetics"

  ],

  "discussion_guide_questions": [

    {

      "question": "How does transhumanism redefine what it means to be human?",

      "start": "00:05:30",

      "end": "00:12:45"

    },

    {

      "question": "Can spiritual evolution coexist with technological advancement?",

      "start": "00:15:20",

      "end": "00:22:10"

    },

    {

      "question": "Where is the line between enhancement and over-reliance on technology?",

      "start": "00:25:00",

      "end": "00:32:15"

    }

  ],

  "key_timeline_insights": [

    {

      "start": "00:05:30",

      "end": "00:12:45",

      "insight": "Introduction to transhumanism and its core concepts, exploring the merging of human and machine"

    },

    {

      "start": "00:15:20",

      "end": "00:22:10",

      "insight": "Discussion of spiritual evolution and consciousness in the context of technological advancement"

    },

    {

      "start": "00:25:00",

      "end": "00:32:15",

      "insight": "Critical examination of the balance between human enhancement and maintaining personal sovereignty"

    }

  ],

  "suggested_metadata": {

    "production_status": ""

  },

  "suggested_content_references": {

    "category_keywords": ["philosophy", "technology", "consciousness", "spirituality"],

    "film_subject_keywords": ["transhumanism", "artificial intelligence", "human evolution"],

    "film_type_keywords": ["interview", "discussion", "talk show"],

    "video_topic_keywords": ["transhumanism", "technology ethics", "consciousness evolution"],

    "conscious_format_keywords": ["interview", "dialogue"],

    "conscious_topic_keywords": ["consciousness", "spiritual evolution", "human potential"],

    "video_collection_keywords": ["Open Minds", "technology and consciousness"]

  },

  "suggested_interview_participants": {

    "host_name": "Regina Meredith",

    "guest_names": ["Ben Stewart"]

  }

}

\`\`\`

`

/**
 * AI chat system prompt (future use)
 * Placeholder for system prompts used in AI chat features
 */
export const AI_CHAT_SYSTEM_PROMPT = ''

/**
 * AI chat persona prompts (future use)
 * Placeholder for persona-specific prompts used in AI chat features
 */
export const AI_CHAT_PERSONA_PROMPTS: Record<string, string> = {}
